import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import { Box } from "@material-ui/core";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import useMediaQuery from "@oriola-origo/core/lib/useMediaQuery";
import { OrigoHeader } from "@oriola-origo/origo-app-shell";
import NotificationPopups from "../notificationPopups/notificationPopups";
import Dialog from "../dialog/dialog";
import NavigationPane from "../navigationPane/navigationPane";
import { isAllowed, Permission, ANY_CUSTOMER } from "../../auth";
import GenericPopup from "../../generic/popup/popup";

const useStyles = makeStyles(theme => ({
  container: {
    overflow: "hidden",
    backgroundColor: theme.palette.background.default,
    "@media print": {
      backgroundColor: "white",
    },
  },
}));

export const APP_HEADER_HEIGHT_MOBILE = 58;
export const APP_HEADER_HEIGHT = 64;
export const NAVIGATION_PANE_WIDTH = 100;
const NAVIGATION_PANE_HEIGHT_MOBILE = 200;

const ensureNoTrailingSlash = url =>
  url.endsWith("/") ? url.slice(0, -1) : url;

function Layout({ children, loadingPanel }) {
  const navigate = useNavigate();
  const isMobileScreen = useMediaQuery("(max-width:960px)");
  const location = useLocation();
  const classes = useStyles();
  const { userData, tokenData } = useSelector(state => state.user);
  const [allowedAddNewCase, setAllowedAddNewCase] = useState(false);
  const [unsavedChangesSettings, setUnsavedChangesSettings] = useState({
    showModal: false,
    lastLocation: null,
  });

  useEffect(() => {
    setAllowedAddNewCase(
      isAllowed(userData, Permission.CASE_CREATE, ANY_CUSTOMER)
    );
  }, [userData]);

  const isFormModified = () =>
    !!document.querySelectorAll(".unsaved-label").length;

  // Browser tab closing or refreshing
  useEffect(() => {
    const handleBeforeUnload = e => {
      if (isFormModified()) {
        e.preventDefault();
        e.returnValue = ""; // Required for Chrome
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  // Track in-app navigation and prompt if needed
  useEffect(() => {
    const unblock = navigate;
    const originalPush = navigate;

    const customNavigate = (to, options = {}) => {
      if (isFormModified()) {
        setUnsavedChangesSettings({
          showModal: true,
          lastLocation: { to, options },
        });
      } else {
        originalPush(to, options);
      }
    };

    // Monkey patch `navigate` if you're calling it in child components
    // Otherwise, pass `customNavigate` to children via context or props
    navigate.custom = customNavigate;

    return () => {
      delete navigate.custom;
    };
  }, [navigate]);

  const handleConfirmNavigationClick = () => {
    setUnsavedChangesSettings({ ...unsavedChangesSettings, showModal: false });
    const { to, options } = unsavedChangesSettings.lastLocation;
    navigate(to, options);
  };

  const handleCancelNavigationClick = () => {
    setUnsavedChangesSettings({ ...unsavedChangesSettings, showModal: false });
  };

  const mainContentHeight = !isMobileScreen
    ? "100vh"
    : `calc(100vh - ${NAVIGATION_PANE_HEIGHT_MOBILE}px)`;

  const getAppHeaderHeight = () =>
    isMobileScreen ? APP_HEADER_HEIGHT_MOBILE : APP_HEADER_HEIGHT;

  const appContentHeight = `calc(100vh - ${getAppHeaderHeight()}px)`;

  return (
    <Box className={classes.container} height="100vh">
      <GenericPopup
        title="discard_changes"
        content="changes_not_saved"
        iconType="camera"
        show={unsavedChangesSettings.showModal}
        confirmTitle="continue_editing"
        cancelTitle="discard"
        onConfirm={handleCancelNavigationClick}
        onCancel={handleConfirmNavigationClick}
        onClose={handleCancelNavigationClick}
      />
      <Box style={{ zIndex: 1000, position: "relative" }}>
        <OrigoHeader
          origoAccessToken={tokenData.accessToken}
          origoUrl={ensureNoTrailingSlash(process.env.REACT_APP_ORIGO_URL)}
          activeApplicationId={process.env.REACT_APP_ORIGO_APP_ID}
          queryParameters={window.location.search}
        />
      </Box>
      <Box
        display="flex"
        flexDirection={isMobileScreen ? "column" : "row"}
        minHeight={appContentHeight}
      >
        <Box order={isMobileScreen ? 2 : 1} zIndex={100}>
          <NavigationPane
            loadingPanel={loadingPanel}
            allowedAddNewCase={allowedAddNewCase}
            isMobileScreen={isMobileScreen}
          />
        </Box>

        <Box
          order={isMobileScreen ? 1 : 2}
          width="100%"
          height={mainContentHeight}
          flexGrow={1}
          py={4}
        >
          {children}
        </Box>
      </Box>
      <NotificationPopups />
      <Dialog />
    </Box>
  );
}

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  loadingPanel: PropTypes.bool,
};

Layout.defaultProps = {
  loadingPanel: false,
};

export default Layout;
